import { ReactNode } from 'react';
import { Box, FormControl, IconButton, makeStyles } from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import DeleteIcon from '@material-ui/icons/Delete';
import { Control, FieldValues, useFieldArray } from 'react-hook-form';
import { WbAlert } from '../WbAlert';
import { WbCardContent } from '../WbCard';
import { WbWidget } from '../WbWidget';

const useStyles = makeStyles(
  theme => ({
    formControl: {
      width: '100%',
    },
    alert: {
      marginBottom: theme.spacing(1),
    },
    formRow: {
      flex: '1',
      display: 'flex',
      gap: theme.spacing(1),
      margin: theme.spacing(1),
    },
    deleteButton: {
      display: 'inline-flex',
      alignItems: 'center',
    },
  }),
  { name: 'WbFieldArray' },
);

interface WbFieldArrayProps<T extends FieldValues> {
  title: string;
  description?: string;
  name: string;
  control: Control<T>;
  defaultItem: any;
  children: (index: number) => ReactNode;
  minItems?: number;
  maxItems?: number;
  disabled?: boolean;
  addButtonLabel?: string;
  showDeleteButton?: boolean;
  onItemAdd?: (newItem: any) => void;
  onItemRemove?: (index: number, item: any) => void;
}

export const WbFieldArray = <T extends FieldValues>({
  title,
  description,
  name,
  control,
  defaultItem,
  children,
  minItems,
  maxItems,
  disabled = false,
  addButtonLabel,
  showDeleteButton = true,
  onItemAdd,
  onItemRemove,
}: WbFieldArrayProps<T>) => {
  const classes = useStyles();

  const { fields, append, remove } = useFieldArray({
    name: name as any,
    control,
  });

  const handleAdd = () => {
    if (maxItems && fields.length >= maxItems) return;

    const newItem =
      typeof defaultItem === 'function' ? defaultItem() : defaultItem;
    append(newItem);
    onItemAdd?.(newItem);
  };

  const handleRemove = (index: number) => {
    const item = fields[index];
    remove(index);
    onItemRemove?.(index, item);
  };

  return (
    <WbWidget
      title={title}
      actions={
        <IconButton
          size="small"
          style={{ marginLeft: 'auto' }}
          onClick={handleAdd}
          aria-label={addButtonLabel || 'Add item'}
        >
          <AddCircleIcon fontSize="small" />
        </IconButton>
      }
    >
      <WbCardContent>
        {description && (
          <WbAlert
            className={classes.alert}
            variant="collapsible"
            message={{
              type: 'info',
              title: 'Suggestion',
              content: description,
            }}
          />
        )}
        <FormControl className={classes.formControl}>
          {fields.map((item, index) => (
            <div key={item.id} style={{ display: 'flex' }}>
              <Box className={classes.formRow}>{children(index)}</Box>

              {showDeleteButton && (
                <div className={classes.deleteButton}>
                  <IconButton
                    size="small"
                    aria-label="Remove"
                    onClick={() => handleRemove(index)}
                    disabled={
                      disabled || (!!minItems && fields.length === minItems)
                    }
                    title={
                      minItems
                        ? 'Remove item'
                        : `Minimum ${minItems} item(s) required`
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                </div>
              )}
            </div>
          ))}
        </FormControl>
      </WbCardContent>
    </WbWidget>
  );
};
